export type ActiveMergeCartFlowPhase = "preview" | "creatingTarget" | "merging" | "refreshing";

export type MergeCartFlow =
  | { phase: "idle" }
  | {
      phase: ActiveMergeCartFlowPhase;
      sourceCartGuid: string;
      sourceContradicted: boolean;
    }
  | {
      phase: "closed";
      sourceCartGuid: string;
    };

export type MergeCartFlowEvent =
  | {
      type: "sessionObserved";
      sourceCartGuid?: string;
      synchronized: boolean;
    }
  | {
      type: "advanced";
      phase: ActiveMergeCartFlowPhase;
    }
  | { type: "invalidated" }
  | { type: "closed" };

export const initialMergeCartFlow: MergeCartFlow = { phase: "idle" };

/**
 * Owns the durable identity and lifecycle of one opened merge.
 *
 * Session Data may discover a flow, but a background fetch cannot close it. Once captured, a
 * different source is recorded as an authoritative contradiction and never replaces the active
 * source. Callers must explicitly advance or close the flow.
 */
export function advanceMergeCartFlow(state: MergeCartFlow, event: MergeCartFlowEvent): MergeCartFlow {
  if (event.type === "sessionObserved") {
    if (!event.synchronized) {
      return state;
    }
    if (!event.sourceCartGuid) {
      return isActiveMergeCartFlow(state)
        ? {
            ...state,
            sourceContradicted: true,
          }
        : state;
    }
    if (state.phase === "idle") {
      return openMergeCartFlow(event.sourceCartGuid);
    }
    if (state.phase === "closed") {
      return state.sourceCartGuid === event.sourceCartGuid ? state : openMergeCartFlow(event.sourceCartGuid);
    }
    return {
      ...state,
      sourceContradicted: state.sourceCartGuid !== event.sourceCartGuid,
    };
  }

  if (!isActiveMergeCartFlow(state)) {
    return state;
  }
  if (event.type === "advanced") {
    return {
      ...state,
      phase: event.phase,
    };
  }
  return closeMergeCartFlow(state);
}

export function isActiveMergeCartFlow(
  state: MergeCartFlow,
): state is Extract<MergeCartFlow, { phase: ActiveMergeCartFlowPhase }> {
  return state.phase !== "idle" && state.phase !== "closed";
}

function openMergeCartFlow(sourceCartGuid: string): MergeCartFlow {
  return {
    phase: "preview",
    sourceCartGuid,
    sourceContradicted: false,
  };
}

function closeMergeCartFlow(state: Extract<MergeCartFlow, { phase: ActiveMergeCartFlowPhase }>): MergeCartFlow {
  return {
    phase: "closed",
    sourceCartGuid: state.sourceCartGuid,
  };
}
